import { Controller, useFormContext } from 'react-hook-form';
import { Input, PhoneInput, TmdxSelect } from '@ds';
import { VEHICLE_TYPES_SELECT_LIST, WarningTypeEnum } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { BaseGroundLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { getFullName, renderSelectItem } from '@/utils/helpers';
import { useBannedDriversAllQuery } from '@/queries/banned-drivers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

export const GroundLegInputs = () => {
  const { isOptionalDataEdit, addWarningMessage, removeWarningMessage } = useLegFormContext();
  const { control, setValue } = useFormContext<BaseGroundLegSchemaValues>();
  const { countryCodes } = useCountriesData();
  const { data: bannedDrivers } = useBannedDriversAllQuery();

  const handleDriverInfoChange = (type: 'driver' | 'phoneNumber', value: string) => {
    setValue(type, value);

    if (value && bannedDrivers?.items && bannedDrivers?.items.length > 0) {
      const isDriverBanned = bannedDrivers.items.find((d) => {
        const valueToCheck = type === 'driver' ? getFullName(d.firstName, d.lastName) : d.phone;

        return valueToCheck?.toLowerCase() === value.toLowerCase();
      });

      if (isDriverBanned) {
        addWarningMessage(WarningTypeEnum.BannedDriver);
      } else {
        removeWarningMessage(WarningTypeEnum.BannedDriver);
      }
    } else {
      removeWarningMessage(WarningTypeEnum.BannedDriver);
    }
  };

  return (
    <>
      <div className="w-full flex flex-row items-center justify-between gap-6">
        <Controller
          name="driver"
          control={control}
          render={({ field: { onChange, ...field }, fieldState: { invalid } }) => (
            <Input
              aria-label="Driver"
              label="Driver"
              placeholder="-"
              isInvalid={invalid}
              isDisabled={!isOptionalDataEdit}
              onValueChange={(value) => handleDriverInfoChange('driver', value)}
              {...field}
            />
          )}
        />
        <div className="w-full">
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, isTouched } }) => (
              <PhoneInput
                countries={countryCodes}
                selectAriaLabel="Code"
                selectLabel="Code"
                inputAriaLabel="Contact"
                label="Contact"
                value={value || ''}
                isInvalid={invalid && isTouched}
                disabled={!isOptionalDataEdit}
                onChange={(phoneNumber) => handleDriverInfoChange('phoneNumber', phoneNumber)}
                {...field}
              />
            )}
          />
        </div>
      </div>
      <div className="w-full flex flex-row items-center justify-between gap-6">
        <Controller
          name="vehicleType"
          control={control}
          render={({ field: { value, ...field }, fieldState: { invalid } }) => (
            <TmdxSelect
              aria-label="Vehicle Type"
              label="Vehicle Type"
              isDisabled={!isOptionalDataEdit}
              disallowEmptySelection
              isInvalid={invalid}
              selectedKeys={value ? [value] : []}
              items={VEHICLE_TYPES_SELECT_LIST}
              {...field}
            >
              {renderSelectItem}
            </TmdxSelect>
          )}
        />
        <Controller
          name="plateNumber"
          control={control}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              aria-label="Plate Number"
              label="Plate Number"
              placeholder="-"
              isInvalid={invalid}
              isDisabled={!isOptionalDataEdit}
              {...field}
            />
          )}
        />
      </div>
      <div className="w-full flex flex-row items-center justify-between gap-6">
        <Controller
          name="email"
          control={control}
          render={({ field: { value, ...field }, fieldState: { invalid } }) => (
            <Input
              aria-label="Email"
              label="Email"
              placeholder="-"
              isInvalid={invalid}
              value={value}
              isDisabled={!isOptionalDataEdit}
              {...field}
            />
          )}
        />
        <Controller
          name="reservationNumber"
          control={control}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              aria-label="Reservation Number"
              label="Reservation Number"
              placeholder="-"
              isInvalid={invalid}
              isDisabled={!isOptionalDataEdit}
              {...field}
            />
          )}
        />
      </div>
      <Controller
        name="pickup"
        control={control}
        render={({ field, fieldState: { invalid } }) => (
          <Input
            aria-label="Pick-up"
            label="Pick-up"
            placeholder="-"
            isInvalid={invalid}
            isDisabled={!isOptionalDataEdit}
            {...field}
          />
        )}
      />
      <Controller
        name="dropoff"
        control={control}
        render={({ field, fieldState: { invalid } }) => (
          <Input
            aria-label="Drop-off"
            label="Drop-off"
            placeholder="-"
            isInvalid={invalid}
            isDisabled={!isOptionalDataEdit}
            {...field}
          />
        )}
      />
      <Controller
        name="wait"
        control={control}
        render={({ field, fieldState: { invalid } }) => (
          <Input
            aria-label="Wait"
            label="Wait"
            placeholder="-"
            isInvalid={invalid}
            isDisabled={!isOptionalDataEdit}
            {...field}
          />
        )}
      />
      <Controller
        name="stay"
        control={control}
        render={({ field, fieldState: { invalid } }) => (
          <Input
            aria-label="Stay"
            label="Stay"
            placeholder="-"
            isInvalid={invalid}
            isDisabled={!isOptionalDataEdit}
            {...field}
          />
        )}
      />
    </>
  );
};
